import axios from 'axios';

class NanoBananaService {
  constructor() {
    this.apiKey = process.env.REACT_APP_NANO_BANANA_API_KEY;
    this.baseURL = process.env.REACT_APP_NANO_BANANA_BASE_URL || 'https://api.nanobanana.com/v1';
    this.client = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json'
      }
    });
  }

  async analyzeImageMetadata(imageFile) {
    try {
      if (!this.apiKey || this.apiKey === 'your_nano_banana_api_key_here') {
        console.warn('Nano Banana API key not configured. Using mock data.');
        return this.generateMockMetadata(imageFile);
      }

      const formData = new FormData();
      formData.append('image', imageFile);

      const response = await this.client.post('/image/analyze', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return this.formatMetadataResponse(response.data);
    } catch (error) {
      console.error('Error analyzing image metadata:', error);
      return this.generateMockMetadata(imageFile);
    }
  }

  formatMetadataResponse(data) {
    return {
      technical: {
        dimensions: data.metadata?.dimensions || { width: 0, height: 0 },
        fileSize: data.metadata?.fileSize || 0,
        format: data.metadata?.format || 'unknown',
        colorSpace: data.metadata?.colorSpace || 'sRGB',
        bitDepth: data.metadata?.bitDepth || 8,
        compression: data.metadata?.compression || 'none',
        dpi: data.metadata?.dpi || { x: 72, y: 72 }
      },
      camera: {
        make: data.exif?.camera?.make || '',
        model: data.exif?.camera?.model || '',
        lens: data.exif?.camera?.lens || '',
        focalLength: data.exif?.camera?.focalLength || '',
        aperture: data.exif?.camera?.aperture || '',
        shutterSpeed: data.exif?.camera?.shutterSpeed || '',
        iso: data.exif?.camera?.iso || '',
        flash: data.exif?.camera?.flash || false
      },
      location: {
        latitude: data.gps?.latitude || null,
        longitude: data.gps?.longitude || null,
        altitude: data.gps?.altitude || null,
        address: data.gps?.address || '',
        city: data.gps?.city || '',
        country: data.gps?.country || ''
      },
      datetime: {
        created: data.datetime?.created || '',
        modified: data.datetime?.modified || '',
        timezone: data.datetime?.timezone || ''
      },
      content: {
        objects: data.analysis?.objects || [],
        faces: data.analysis?.faces || [],
        text: data.analysis?.text || [],
        colors: data.analysis?.dominantColors || [],
        mood: data.analysis?.mood || '',
        quality: data.analysis?.quality || ''
      },
      editing: {
        brightness: data.adjustments?.brightness || 0,
        contrast: data.adjustments?.contrast || 0,
        saturation: data.adjustments?.saturation || 0,
        hue: data.adjustments?.hue || 0,
        gamma: data.adjustments?.gamma || 1.0,
        exposure: data.adjustments?.exposure || 0,
        highlights: data.adjustments?.highlights || 0,
        shadows: data.adjustments?.shadows || 0,
        temperature: data.adjustments?.temperature || 0,
        tint: data.adjustments?.tint || 0
      }
    };
  }

  generateMockMetadata(imageFile) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          technical: {
            dimensions: { width: 1920, height: 1080 },
            fileSize: imageFile?.size || 2048576,
            format: imageFile?.type?.split('/')[1] || 'jpeg',
            colorSpace: 'sRGB',
            bitDepth: 8,
            compression: 'JPEG',
            dpi: { x: 300, y: 300 }
          },
          camera: {
            make: 'Canon',
            model: 'EOS R5',
            lens: 'RF 24-70mm f/2.8L IS USM',
            focalLength: '50mm',
            aperture: 'f/2.8',
            shutterSpeed: '1/125s',
            iso: 'ISO 400',
            flash: false
          },
          location: {
            latitude: 40.7128,
            longitude: -74.0060,
            altitude: 10,
            address: '123 Demo Street',
            city: 'New York',
            country: 'United States'
          },
          datetime: {
            created: '2024-01-15T14:30:00Z',
            modified: '2024-01-15T14:35:00Z',
            timezone: 'UTC-5'
          },
          content: {
            objects: [
              { name: 'person', confidence: 0.95, bbox: { x: 100, y: 150, width: 200, height: 300 } },
              { name: 'building', confidence: 0.87, bbox: { x: 400, y: 50, width: 500, height: 400 } },
              { name: 'car', confidence: 0.92, bbox: { x: 50, y: 400, width: 150, height: 100 } }
            ],
            faces: [
              { bbox: { x: 150, y: 180, width: 80, height: 100 }, age: 25, gender: 'female', emotion: 'happy' }
            ],
            text: [
              { content: 'DEMO CAFE', bbox: { x: 450, y: 100, width: 200, height: 50 } },
              { content: 'OPEN 9AM-5PM', bbox: { x: 470, y: 160, width: 150, height: 30 } }
            ],
            colors: [
              { hex: '#4A90E2', percentage: 25 },
              { hex: '#7ED321', percentage: 20 },
              { hex: '#F5A623', percentage: 15 },
              { hex: '#D0021B', percentage: 10 },
              { hex: '#9013FE', percentage: 30 }
            ],
            mood: 'cheerful',
            quality: 'high'
          },
          editing: {
            brightness: 5,
            contrast: 10,
            saturation: -2,
            hue: 0,
            gamma: 1.0,
            exposure: 0.3,
            highlights: -10,
            shadows: 15,
            temperature: 200,
            tint: -5
          }
        });
      }, 1500);
    });
  }

  async updateImageMetadata(imageFile, metadata, edits = {}) {
    try {
      if (!this.apiKey || this.apiKey === 'your_nano_banana_api_key_here') {
        console.warn('Nano Banana API key not configured. Using mock editing.');
        return this.generateMockEditedImage(imageFile, edits);
      }

      const formData = new FormData();
      formData.append('image', imageFile);
      formData.append('edits', JSON.stringify(edits));

      const response = await this.client.post('/image/edit', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        responseType: 'blob'
      });

      return {
        editedImage: response.data,
        downloadUrl: URL.createObjectURL(response.data)
      };
    } catch (error) {
      console.error('Error editing image:', error);
      return this.generateMockEditedImage(imageFile, edits);
    }
  }

  generateMockEditedImage(originalFile, edits) {
    return new Promise((resolve) => {
      setTimeout(() => {
        // Create a mock edited image (in real implementation, this would be the actual edited image)
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = 400;
        canvas.height = 300;
        
        // Apply mock visual effects based on edits
        ctx.fillStyle = this.calculateMockColor(edits);
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Add some mock content
        ctx.fillStyle = '#ffffff';
        ctx.font = '20px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('EDITED IMAGE', canvas.width / 2, canvas.height / 2);
        ctx.fillText('(Demo Mode)', canvas.width / 2, canvas.height / 2 + 30);
        
        canvas.toBlob((blob) => {
          resolve({
            editedImage: blob,
            downloadUrl: URL.createObjectURL(blob)
          });
        }, originalFile?.type || 'image/jpeg');
      }, 2000);
    });
  }

  calculateMockColor(edits) {
    let r = 128 + (edits.brightness || 0) * 2;
    let g = 128 + (edits.contrast || 0) * 2;
    let b = 128 + (edits.saturation || 0) * 2;
    
    r = Math.max(0, Math.min(255, r));
    g = Math.max(0, Math.min(255, g));
    b = Math.max(0, Math.min(255, b));
    
    return `rgb(${r}, ${g}, ${b})`;
  }

  async removeBackground(imageFile) {
    try {
      if (!this.apiKey || this.apiKey === 'your_nano_banana_api_key_here') {
        console.warn('Nano Banana API key not configured. Using mock background removal.');
        return this.generateMockBackgroundRemoval(imageFile);
      }

      const formData = new FormData();
      formData.append('image', imageFile);

      const response = await this.client.post('/image/remove-background', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        responseType: 'blob'
      });

      return {
        processedImage: response.data,
        downloadUrl: URL.createObjectURL(response.data)
      };
    } catch (error) {
      console.error('Error removing background:', error);
      return this.generateMockBackgroundRemoval(imageFile);
    }
  }

  generateMockBackgroundRemoval(originalFile) {
    return new Promise((resolve) => {
      setTimeout(() => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = 400;
        canvas.height = 300;
        
        // Create a transparent background effect
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Draw a mock subject with transparent background
        ctx.fillStyle = '#4A90E2';
        ctx.beginPath();
        ctx.ellipse(canvas.width / 2, canvas.height / 2, 80, 120, 0, 0, 2 * Math.PI);
        ctx.fill();
        
        ctx.fillStyle = '#ffffff';
        ctx.font = '16px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Background Removed', canvas.width / 2, canvas.height / 2);
        ctx.fillText('(Demo Mode)', canvas.width / 2, canvas.height / 2 + 20);
        
        canvas.toBlob((blob) => {
          resolve({
            processedImage: blob,
            downloadUrl: URL.createObjectURL(blob)
          });
        }, 'image/png');
      }, 3000);
    });
  }

  isConfigured() {
    return this.apiKey && this.apiKey !== 'your_nano_banana_api_key_here';
  }
}

const nanoBananaService = new NanoBananaService();
export default nanoBananaService;